import React, { useEffect, useRef, useState } from "react";

import { useRouter } from "next/router";

import { Auth } from "aws-amplify";
import { AuthenticationDetails } from "amazon-cognito-identity-js";

import ReCAPTCHA from "react-google-recaptcha";

import axiosParent from "utilities/axiosInstance";
import { decodeJWTTokenInServer, isDisabledAccountError, DISABLED_ACCOUNT_MESSAGE } from "utilities/AuthenticationUtilities";
import { analytics, tryGetRailsJwtFromStorage } from "utilities/analytics";
import { markEntry, LAUNCHER_PATH } from "utilities/entryRouting";
import { Button } from "components/Standalone/Button";
import { FieldLabel, FieldMessage, PasswordField, TextField, TEXT_LINK_CLASS } from "components/Form/SignInFields";

const SIGN_IN_LABEL = "Sign in";
const SIGNING_IN_LABEL = "Signing in…";
const SUBMIT_CODE_LABEL = "Submit code";
const SUBMITTING_CODE_LABEL = "Submitting…";

/**
 * The "Previous login" (email/password through Cognito) kept during the
 * transition to "Sign in with OMCi": credentials → optional one-time code →
 * optional forced password change, then the same post-sign-in routing as SSO.
 * Nothing is autofocused: the primary route sits above this form.
 *
 * @param {object} props
 * @param {string} props.forgotPasswordHref Cognito hosted-UI password recovery (previous account only).
 */
export default function Form({ forgotPasswordHref }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [disable, setDisable] = useState(false);
    const [buttonText, setButtonText] = useState(SIGN_IN_LABEL);
    const [requestError, setRequestError] = useState("");

    const [isWrongPassword, setIsWrongPassword] = useState(false);
    const [isAccountDisabled, setIsAccountDisabled] = useState(false);

    const [user, setUser] = useState(null);
    const [mfaType, setMFAType] = useState(null);

    const [code, setCode] = useState("");
    const [showCode, setShowCode] = useState(false);
    const [isWrongCode, setIsWrongCode] = useState(false);

    const [message, setMessage] = useState(null);

    const baseUrl = process.env.NEXT_PUBLIC_BACKEND_PATH;
    const router = useRouter();

    const [error, setError] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState("");
    const [tooManyConfirmsError, setTooManyConfirmsError] = useState(false);
    const [verificationError, setVerificationError] = useState(false);
    const [passwordContentsError, setPasswordContentsError] = useState("");
    const [showPasswordChange, setShowPasswordChange] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [newPassword, setNewPassword] = useState("");
    const [noMailError, setNoMailError] = useState(false);
    const [mfaButtonText, setMfaButtonText] = useState(SUBMIT_CODE_LABEL);

    const recapthaRef = useRef(null);
    const [isVerified, setIsVerified] = useState(false);

    async function handleCaptchaSubmission(token) {
        try {
            if (token) {
                const resp = await fetch("/api/register/captcha", {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ token }),
                });

                const data = await resp.json();

                if (resp.ok && data.message === "Success") {
                    setIsVerified(true);
                } else {
                    setIsVerified(false);
                    console.error("reCAPTCHA verification failed:", data);

                    if (recapthaRef.current) {
                        recapthaRef.current.reset();
                    }
                }
            }
        } catch (e) {
            console.error("reCAPTCHA error:", e);
            setIsVerified(false);
            if (recapthaRef.current) {
                recapthaRef.current.reset();
            }
        }
    }

    const handleChange = (token) => {
        handleCaptchaSubmission(token);
    };

    function handleExpired() {
        setIsVerified(false);
    }

    const handleLogin = async (e, email, password) => {
        e.preventDefault();
        setDisable(true);
        try {
            setButtonText(SIGNING_IN_LABEL);
            setMessage(null);

            const user = await Auth.signIn(email, password);
            setUser(user);
            if (user.challengeName === "SOFTWARE_TOKEN_MFA" || user.challengeName === "SMS_MFA") {
                setDisable(false);
                setShowCode(true);
                setMFAType(user.challengeName);
            } else if (user.challengeName === "MFA_SETUP") {
                setMessage("To sign in, please finish setting up your 2-Factor Authentication method.");
            } else if (user.challengeName === null || user.challengeName === undefined) {
                const cognitoJwt = (await Auth.currentSession()).getIdToken().getJwtToken();
                const refreshJwt = (await Auth.currentSession()).getRefreshToken().getToken();
                await decodeJWTTokenInServer(baseUrl + "/api/users/login", postLoginTransition, cognitoJwt, refreshJwt);
            } else if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
                if (user.username == null || user.username == "") {
                    setNoMailError(true);
                } else {
                    setCurrentUser(user);
                    setNewPassword("");
                    setConfirmPassword("");
                    setShowPasswordChange(true);
                    setError(false);
                    setTooManyConfirmsError(false);
                    setVerificationError(false);
                    setPasswordContentsError("");
                }
            }
        } catch (error) {
            if (error.name == "PasswordResetRequiredException") {
                setMessage("Password reset required for user due to security reasons. To begin the process, press 'Forgot password?'");
                setDisable(false);
                setButtonText(SIGN_IN_LABEL);
            } else if (isDisabledAccountError(error)) {
                // Disabled account (Cognito or portal): not a credentials problem, so no red
                // border on the password field and a message that points at the administrator.
                console.log(error);
                setPassword("");

                setDisable(false);
                setMfaButtonText(SUBMIT_CODE_LABEL);
                setButtonText(SIGN_IN_LABEL);

                setIsAccountDisabled(true);
            } else {
                console.log(error);
                setPassword("");

                setDisable(false);
                setMfaButtonText(SUBMIT_CODE_LABEL);
                setButtonText(SIGN_IN_LABEL);

                setIsWrongPassword(true);
            }
        }
    };

    const handlePasswordChange = async () => {
        setDisable(false);
        setButtonText(SIGN_IN_LABEL);
        setPassword("");
        if (newPassword !== confirmPassword) setIsWrongPassword(true);
        else {
            let base_error = "";
            if (newPassword.length < 8 || newPassword.length > 99) base_error = "Password must be between 8 and 99 characters in length.\n";
            if (!newPassword.match(/(?=.*[a-z])/)) base_error += "Password must contain at least one lowercase character.\n";
            if (!newPassword.match(/(?=.*[A-Z])/)) base_error += "Password must contain at least one uppercase character.\n";
            if (!newPassword.match(/(?=.*[0-9])/)) base_error += "Password must contain at least one digit.\n";
            if (!newPassword.match(/(?=.*[\^$*.\[\]{}\(\)?\-\"!@#%&\/,><\':;|_~`])/))
                base_error += "Password must contain at least one character from the following list: \n ^ $ * . [ ] { } ( ) ? \" ! @ # % & /  , > < ' : ; | _ ~ ` \n";
            if (newPassword.match(/[^a-zA-Z0-9\^$*.\[\]{}\(\)?\-\"!@#%&\/,><\':;|_~`]/)) {
                base_error += "Password must not contain invalid characters. ";
                let aux = newPassword;
                while (aux.match(/[a-zA-Z0-9\^$*.\[\]{}\(\)?\-\"!@#%&\/,><\':;|_~`]/)) {
                    aux = aux.replace(/[a-zA-Z0-9\^$*.\[\]{}\(\)?\-\"!@#%&\/,><\':;|_~`]/, "");
                }
                let invalidChars = aux
                    .split("")
                    .filter(function (item, pos, self) {
                        return self.indexOf(item) == pos;
                    })
                    .join("");
                base_error += "Invalid characters: '" + invalidChars + "'";
            }
            if (base_error != "") setPasswordContentsError(base_error);
            else {
                const authenticationData = {
                    Username: currentUser.username,
                    Password: password,
                };
                const authenticationDetails = new AuthenticationDetails(authenticationData);
                currentUser.authenticateUser(authenticationDetails, {
                    newPasswordRequired: () => {
                        Auth.completeNewPassword(currentUser, newPassword, {
                            name: currentUser.username,
                        })
                            .then((user) => {
                                setDisable(false);
                                setShowPasswordChange(false);
                            })
                            .catch((error) => {
                                setRequestError(error.message);
                                console.log(error.message);
                            });
                    },
                    onFailure: (error) => {
                        setRequestError(error.message);
                        console.log(error);
                    },
                    onSuccess: (msj) => {},
                });
            }
        }
    };

    const handleCode = async (e, user, code, mfaType) => {
        try {
            e.preventDefault();
            setDisable(true);
            const loggedUser = await Auth.confirmSignIn(
                user, // Return object from Auth.signIn()
                code, // Confirmation code
                mfaType, // MFA Type e.g. SMS_MFA, SOFTWARE_TOKEN_MFA
            );

            const cognitoJwt = (await Auth.currentSession()).getIdToken().getJwtToken();
            const refreshJwt = (await Auth.currentSession()).getRefreshToken().getToken();
            await decodeJWTTokenInServer(baseUrl + "/api/users/login", postLoginTransition, cognitoJwt, refreshJwt);
        } catch (error) {
            console.log(error);
            setCode("");

            setDisable(false);
            setButtonText(SIGN_IN_LABEL);
            setMfaButtonText(SUBMIT_CODE_LABEL);

            if (isDisabledAccountError(error)) {
                // The code was accepted; the portal refused the account. Return to the
                // sign-in form with the account message instead of blaming the code.
                setShowCode(false);
                setPassword("");
                setIsAccountDisabled(true);
            } else {
                setIsWrongCode(true);
            }
        }
    };

    const postLoginTransition = async () => {
        const pathname = LAUNCHER_PATH;
        try {
            const jwt = tryGetRailsJwtFromStorage() || (await Auth.currentSession()).getIdToken().getJwtToken();
            analytics.identifyWithBackendUser({ jwt });
            analytics.track("user_login", { login_method: "password" });
        } catch {
            try {
                analytics.syncIdentityFromRailsStorage();
                analytics.track("user_login", { login_method: "password" });
            } catch {
                /* fail-open */
            }
        }
        // Fresh sign-in: the Launch Pad routes to the user's destination and asks
        // for the port again (SPEC-007; consumed by pages/users/pad.jsx).
        markEntry({ fresh: true });
        router.push(pathname);
    };

    useEffect(() => {
        const checkSession = async () => {
            try {
                // Fetch session data from the server
                const response = await axiosParent().post(
                    process.env.NEXT_PUBLIC_BACKEND_PATH + "/api/logged_in",
                    {
                        expected_admin: false,
                        pathname: "/users/pad",
                    },
                    {
                        headers: {
                            "Content-Type": "application/json",
                        },
                        withCredentials: true,
                    },
                );
                // Destructure required data
                const { context, last_admin_path, last_path, last_layout, name, job_title, email, language, dark_mode, show_sidebar } = response.data;
                document.cookie = "language=" + language + ";path=/";
                document.cookie = (dark_mode ? "mode=dark" : "mode=light") + ";path=/";
                document.cookie = (show_sidebar ? "show_sidebar=true" : "show_sidebar=false") + ";path=/";
                document.cookie = "context=" + context + ";path=/";
                document.cookie = "last_admin_path=" + last_admin_path + ";path=/";
                document.cookie = "last_path=" + last_path + ";path=/";
                document.cookie = "last_layout=" + last_layout + ";path=/";
                document.cookie = "user=" + name + ";path=/";
                document.cookie = "job_title=" + encodeURIComponent(job_title || "") + ";path=/";

                try {
                    const jwt = tryGetRailsJwtFromStorage();
                    if (jwt) {
                        analytics.identifyWithBackendUser({ jwt, name, email });
                    } else {
                        analytics.syncIdentityFromRailsStorage();
                    }
                    analytics.track("user_login", { login_method: "existing_session" });
                } catch {
                    /* fail-open */
                }

                // A valid session continues to its destination — no launcher step,
                // and the remembered port is kept (SPEC-007 AC3).
                markEntry({ fresh: false });
                router.push(LAUNCHER_PATH);
            } catch (error) {
                // console.log("Error fetching session data:", error);
            }
        };

        checkSession();
    }, []);

    const backFromCode = () => {
        setShowCode(false);
        setPassword("");
        setEmail("");
        setCode("");
        setButtonText(SIGN_IN_LABEL);
        setMfaButtonText(SUBMIT_CODE_LABEL);
        setIsWrongCode(false);
        setIsAccountDisabled(false);
        setMessage(null);
        setUser(null);
        setMFAType(false);
    };

    if (showPasswordChange) {
        return (
            <form
                onSubmit={(formEvent) => {
                    formEvent.preventDefault();
                    handlePasswordChange();
                }}
                className="space-y-4"
                data-testid="previous-login-new-password"
            >
                <div>
                    <FieldLabel htmlFor="new_password">New password</FieldLabel>
                    <div className="mt-2">
                        <PasswordField
                            id="new_password"
                            name="new_password"
                            autoComplete="new-password"
                            value={newPassword}
                            onChange={(passwordEvent) => {
                                setNewPassword(passwordEvent.target.value);
                                setIsWrongPassword(false);
                            }}
                        />
                    </div>
                </div>
                <div>
                    <FieldLabel htmlFor="confirm_password">Confirm new password</FieldLabel>
                    <div className="mt-2">
                        <PasswordField
                            id="confirm_password"
                            name="confirm_password"
                            autoComplete="new-password"
                            invalid={isWrongPassword}
                            value={confirmPassword}
                            onChange={(passwordEvent) => {
                                setConfirmPassword(passwordEvent.target.value);
                                setIsWrongPassword(false);
                            }}
                        />
                    </div>
                    {isWrongPassword && <FieldMessage>The passwords do not match.</FieldMessage>}
                    {requestError.length > 0 && <FieldMessage>{requestError}</FieldMessage>}
                    {error && <FieldMessage>An error has occurred.</FieldMessage>}
                    {tooManyConfirmsError && <FieldMessage>There have been too many requests. Please wait a bit and try again.</FieldMessage>}
                    {verificationError && <FieldMessage>The verification code is invalid. Please check your email for the verification code.</FieldMessage>}
                    {passwordContentsError != "" && <FieldMessage>{passwordContentsError}</FieldMessage>}
                </div>
                <Button type="submit" variant="secondary" size="medium" className="w-full">
                    Set password
                </Button>
                <div className="text-center">
                    <button
                        type="button"
                        onClick={() => {
                            setShowPasswordChange(false);
                            setDisable(false);
                            setButtonText(SIGN_IN_LABEL);
                            setPassword("");
                        }}
                        className={TEXT_LINK_CLASS}
                    >
                        Back to sign in
                    </button>
                </div>
            </form>
        );
    }

    if (showCode) {
        return (
            <form
                onSubmit={(formEvent) => {
                    setMfaButtonText(SUBMITTING_CODE_LABEL);
                    handleCode(formEvent, user, code, mfaType);
                }}
                className="space-y-4"
                data-testid="previous-login-code"
            >
                <div>
                    <FieldLabel htmlFor="code">One-time password</FieldLabel>
                    <div className="mt-2">
                        <TextField
                            id="code"
                            name="code"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            invalid={isWrongCode}
                            value={code}
                            onChange={(codeEvent) => {
                                setCode(codeEvent.target.value);
                                setIsWrongCode(false);
                            }}
                        />
                    </div>
                    {isWrongCode && <FieldMessage>The code you entered is invalid, or has expired. Please try again.</FieldMessage>}
                </div>
                <Button type="submit" variant="secondary" size="medium" className="w-full" disabled={!code || disable}>
                    {mfaButtonText}
                </Button>
                <div className="text-center">
                    <button type="button" onClick={() => backFromCode()} className={TEXT_LINK_CLASS}>
                        Back to sign in
                    </button>
                </div>
            </form>
        );
    }

    return (
        <form onSubmit={(formEvent) => handleLogin(formEvent, email, password)} className="space-y-4" data-testid="previous-login">
            <div>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <div className="mt-2">
                    <TextField
                        id="email"
                        name="email"
                        type="text"
                        inputMode="email"
                        autoComplete="username"
                        autoCapitalize="none"
                        spellCheck={false}
                        value={email}
                        onChange={(emailEvent) => {
                            setEmail(emailEvent.target.value);
                            setIsWrongPassword(false);
                            setIsAccountDisabled(false);
                            setNoMailError(false);
                        }}
                    />
                </div>
            </div>

            <div>
                <FieldLabel htmlFor="password">Password</FieldLabel>
                <div className="mt-2">
                    <PasswordField
                        id="password"
                        name="password"
                        autoComplete="current-password"
                        invalid={isWrongPassword}
                        value={password}
                        onChange={(passwordEvent) => {
                            setPassword(passwordEvent.target.value);
                            setIsWrongPassword(false);
                            setIsAccountDisabled(false);
                            setNoMailError(false);
                        }}
                    />
                </div>
                {message !== null && <FieldMessage>{message}</FieldMessage>}
                {isWrongPassword && <FieldMessage>The credentials that were submitted are incorrect.</FieldMessage>}
                {isAccountDisabled && <FieldMessage>{DISABLED_ACCOUNT_MESSAGE}</FieldMessage>}
                {noMailError && <FieldMessage>This account has no email address associated to it. Please contact support.</FieldMessage>}
            </div>

            <div className="flex justify-end">
                <a href={forgotPasswordHref} className={TEXT_LINK_CLASS} data-testid="forgot-password">
                    Forgot password?
                </a>
            </div>

            {/* Vendor-controlled widget: the live reCAPTCHA checkbox with its own branding (see styles/globals.css for narrow screens). */}
            <div className="login-recaptcha">
                <ReCAPTCHA sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ""} ref={recapthaRef} onChange={handleChange} onExpired={handleExpired} />
            </div>

            <Button type="submit" variant="secondary" size="medium" className="w-full" disabled={!email || !password || disable || !isVerified} data-testid="previous-login-submit">
                {buttonText}
            </Button>
        </form>
    );
}
